import { apiFetch } from './apiFetch';

export interface TranscriptionResponse {
  text: string;
  fileId?: string;
}

// WKWebView's native fetch corrupts binary Blob bodies (bytes >127 get UTF-8 mangled).
// Sending audio as base64 inside JSON bypasses the bridge serialization bug.
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read audio'));
        return;
      }
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read audio'));
    reader.readAsDataURL(blob);
  });

const createTranscription = async (audioBase64: string, filename: string): Promise<TranscriptionResponse> => {
  const res = await apiFetch(`/transcriptions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, audioBase64 }),
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(err?.error ?? err?.message ?? res.statusText);
  }

  return res.json() as Promise<TranscriptionResponse>;
};

/**
 * POST /transcriptions from a Blob/File (web path).
 * Auth: the khova-api session (cookie on web, bearer on native). Max 25 MB of decoded audio.
 */
export const createTranscriptionFromFile = async (
  file: File | Blob,
  filename?: string,
): Promise<TranscriptionResponse> => {
  const name = filename ?? (file instanceof File ? file.name : 'recording.webm');
  const audioBase64 = await blobToBase64(file);
  return createTranscription(audioBase64, name);
};

/**
 * POST /transcriptions from an already-encoded base64 string (native path).
 * Skips the Blob→base64 conversion when the plugin already provides base64.
 * Auth: the khova-api session (cookie on web, bearer on native). Max 25 MB of decoded audio.
 */
export const createTranscriptionFromBase64 = (audioBase64: string, filename: string): Promise<TranscriptionResponse> =>
  createTranscription(audioBase64, filename);
